import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  Modal,
  TextField,
  Box,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  MenuItem,
  Switch,
  Chip,
  Divider,
  Icon,
  IconButton,
  Tooltip,
  InputAdornment,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "../../utils/axiosConfig";
import { fadeInUp, fadeInDown, modalScaleIn } from "../../utils/animations";

const API_TEXTES = "/api/textes/";
const API_DOMAINES = "/api/domaines/";
const API_TYPES = "/api/types-textes/";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: "90vw", md: 600 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  maxHeight: "90vh",
  overflowY: "auto",
};

// --- SOUS-COMPOSANTS ---

const ActionButtons = ({ row, onEdit, onDelete, onViewDocs, onAnalyse }) => (
  <MDBox display="flex" gap={0.3}>
    <Tooltip title="Modifier" arrow>
      <IconButton size="small" onClick={() => onEdit(row.original)} sx={{ color: "#1976d2" }}>
        <Icon sx={{ fontSize: "18px !important" }}>edit</Icon>
      </IconButton>
    </Tooltip>
    <Tooltip title="Documents" arrow>
      <IconButton size="small" onClick={() => onViewDocs(row.original)} sx={{ color: "#ed6c02" }}>
        <Icon sx={{ fontSize: "18px !important" }}>folder_open</Icon>
      </IconButton>
    </Tooltip>
    <Tooltip title="Analyser" arrow>
      <IconButton size="small" onClick={() => onAnalyse(row.original.id)} sx={{ color: "#2e7d32" }}>
        <Icon sx={{ fontSize: "18px !important" }}>manage_search</Icon>
      </IconButton>
    </Tooltip>
    <Tooltip title="Supprimer" arrow>
      <IconButton size="small" onClick={() => onDelete(row.original.id)} sx={{ color: "#d32f2f" }}>
        <Icon sx={{ fontSize: "18px !important" }}>delete</Icon>
      </IconButton>
    </Tooltip>
  </MDBox>
);

ActionButtons.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewDocs: PropTypes.func.isRequired,
  onAnalyse: PropTypes.func.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
};

function TextesReglementaires() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [domaines, setDomaines] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchFilter, setSearchFilter] = useState("");
  const [domaineFilter, setDomaineFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [formData, setFormData] = useState({
    id: null,
    titre: "",
    reference: "",
    resume: "",
    date_publication: "",
    domaine_id: "",
    type_texte_id: "",
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });

  // Progression download / upload
  const [downloadProgress, setDownloadProgress] = useState({
    open: false,
    progress: 0,
    filename: "",
  });
  const [uploadProgress, setUploadProgress] = useState(0);

  // Modal Documents
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedTexte, setSelectedTexte] = useState(null);
  const [versions, setVersions] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [resTextes, resDomaines, resTypes] = await Promise.all([
        api.get(API_TEXTES),
        api.get(API_DOMAINES),
        api.get(API_TYPES),
      ]);
      setRows(resTextes.data);
      setDomaines(resDomaines.data);
      setTypes(resTypes.data);
    } catch (error) {
      setSnackbar({ open: true, message: "Erreur de chargement des données.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpen = () => {
    setIsEdit(false);
    setSelectedFile(null);
    setFormData({
      id: null,
      titre: "",
      reference: "",
      resume: "",
      date_publication: "",
      domaine_id: "",
      type_texte_id: "",
    });
    setOpen(true);
  };

  const handleViewDocs = useCallback(async (rowData) => {
    setSelectedTexte(rowData);
    setDocsOpen(true);
    setDocsLoading(true);
    try {
      const res = await api.get(`${API_TEXTES}${rowData.id}/versions`);
      setVersions(res.data);
    } catch (error) {
      setSnackbar({ open: true, message: "Erreur chargement des versions.", severity: "error" });
    } finally {
      setDocsLoading(false);
    }
  }, []);

  const handleToggleVersion = useCallback(
    async (versionId) => {
      try {
        const res = await api.patch(`${API_TEXTES}version/${versionId}/toggle`);
        setSnackbar({ open: true, message: res.data.message, severity: "success" });
        // Refresh versions list
        if (selectedTexte) {
          const updated = await api.get(`${API_TEXTES}${selectedTexte.id}/versions`);
          setVersions(updated.data);
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Erreur lors du changement de statut.",
          severity: "error",
        });
      }
    },
    [selectedTexte]
  );

  const handleDownload = useCallback(async (doc) => {
    setDownloadProgress({ open: true, progress: 0, filename: doc.nom_fichier });
    try {
      const res = await api.get(`/api/textes/document/download/${doc.id}`, {
        responseType: "blob",
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setDownloadProgress((prev) => ({ ...prev, progress: pct }));
          }
        },
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = doc.nom_fichier;
      link.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      setSnackbar({ open: true, message: "Erreur lors du téléchargement.", severity: "error" });
    } finally {
      setDownloadProgress({ open: false, progress: 0, filename: "" });
    }
  }, []);

  const handleDeleteVersion = useCallback(
    (versionId, versionLabel) => {
      setConfirmDialog({
        open: true,
        message: `Supprimer définitivement la version ${versionLabel} et tous ses fichiers ? Cette action est irréversible.`,
        onConfirm: async () => {
          try {
            await api.delete(`${API_TEXTES}version/${versionId}`);
            setSnackbar({
              open: true,
              message: "Version supprimée définitivement.",
              severity: "success",
            });
            if (selectedTexte) {
              const updated = await api.get(`${API_TEXTES}${selectedTexte.id}/versions`);
              setVersions(updated.data);
            }
          } catch (error) {
            setSnackbar({
              open: true,
              message: error.response?.data?.detail || "Erreur lors de la suppression.",
              severity: "error",
            });
          }
        },
      });
    },
    [selectedTexte]
  );

  const handleEdit = useCallback((rowData) => {
    setIsEdit(true);
    setSelectedFile(null);
    setFormData({
      id: rowData.id,
      titre: rowData.titre,
      reference: rowData.reference,
      resume: rowData.resume || "",
      date_publication: rowData.date_publication || "",
      domaine_id: rowData.domaine_id || "",
      type_texte_id: rowData.type_texte_id || "",
    });
    setOpen(true);
  }, []);

  const handleDelete = useCallback(
    (id) => {
      setConfirmDialog({
        open: true,
        message: "Voulez-vous supprimer ce texte ?",
        onConfirm: async () => {
          try {
            await api.delete(`${API_TEXTES}${id}`);
            setSnackbar({ open: true, message: "Texte supprimé !", severity: "success" });
            fetchData();
          } catch (error) {
            setSnackbar({
              open: true,
              message: "Erreur lors de la suppression.",
              severity: "error",
            });
          }
        },
      });
    },
    [fetchData]
  );

  const handleAnalyse = useCallback((id) => navigate(`/analyse-texte/${id}`), [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !selectedFile) {
      setSnackbar({ open: true, message: "Un fichier PDF est requis.", severity: "warning" });
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);
    try {
      const data = new FormData();
      data.append("titre", formData.titre);
      data.append("reference", formData.reference);
      data.append("resume", formData.resume);
      data.append("date_publication", formData.date_publication);
      data.append("domaine_id", formData.domaine_id);
      data.append("type_texte_id", formData.type_texte_id);

      if (selectedFile) {
        data.append("file", selectedFile);
      }

      const uploadConfig = {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(pct);
          }
        },
      };

      if (isEdit) {
        await api.put(`${API_TEXTES}${formData.id}`, data, uploadConfig);
        setSnackbar({ open: true, message: "Mise à jour réussie !", severity: "success" });
      } else {
        await api.post(API_TEXTES, data, uploadConfig);
        setSnackbar({ open: true, message: "Création réussie !", severity: "success" });
      }

      setOpen(false);
      fetchData();
    } catch (error) {
      let message = "Erreur lors de l'enregistrement.";

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;

        // 🔥 Cas FastAPI (liste d'erreurs)
        if (Array.isArray(detail)) {
          message = detail.map((err) => err.msg).join(" | ");
        }
        // 🔥 Cas simple (string)
        else if (typeof detail === "string") {
          message = detail;
        }
      }

      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      // 3. CRUCIAL : On libère le bouton QUOI QU'IL ARRIVE
      setIsSubmitting(false);
    }
  };

  const handleToggleAnalyse = useCallback(
    async (texteId) => {
      try {
        const res = await api.patch(`${API_TEXTES}${texteId}/toggle-analyse`);
        setSnackbar({ open: true, message: res.data.message, severity: "success" });
        fetchData();
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Erreur lors du changement de statut d'analyse.",
          severity: "error",
        });
      }
    },
    [fetchData]
  );

  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "id", width: "6%" },
      { Header: "Titre", accessor: "titre", width: "28%", wrap: true },
      {
        Header: "Informations",
        accessor: "domaine_id",
        wrap: true,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => {
          /* eslint-disable react/prop-types */
          const item = row.original;
          const domaine = domaines.find((d) => d.id === item.domaine_id)?.nom || "N/A";
          const type = types.find((t) => t.id === item.type_texte_id)?.type || "N/A";
          const analysed = item.est_analyse;
          return (
            <MDBox sx={{ minWidth: 170 }}>
              <MDBox display="flex" alignItems="center" gap={0.6} mb={0.5}>
                <Icon sx={{ fontSize: "13px !important", color: "#9e9e9e", flexShrink: 0 }}>
                  tag
                </Icon>
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {item.reference || "-"}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={0.6} mb={0.5}>
                <Icon sx={{ fontSize: "13px !important", color: "#9e9e9e", flexShrink: 0 }}>
                  calendar_today
                </Icon>
                <MDTypography variant="caption" color="text">
                  {item.date_publication || "-"}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={0.6} mb={0.5}>
                <Icon sx={{ fontSize: "13px !important", color: "#9e9e9e", flexShrink: 0 }}>
                  domain
                </Icon>
                <MDTypography variant="caption" color="text">
                  {domaine}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={0.6} mb={0.6}>
                <Icon sx={{ fontSize: "13px !important", color: "#9e9e9e", flexShrink: 0 }}>
                  description
                </Icon>
                <MDTypography variant="caption" color="text">
                  {type}
                </MDTypography>
              </MDBox>
              <Chip
                label={analysed ? "Analysé" : "Non analysé"}
                size="small"
                onClick={() => handleToggleAnalyse(item.id)}
                sx={{
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontSize: "0.65rem",
                  height: 20,
                  backgroundColor: analysed ? "#e8f5e9" : "#fff3e0",
                  color: analysed ? "#2e7d32" : "#e65100",
                  border: "1px solid",
                  borderColor: analysed ? "#a5d6a7" : "#ffcc80",
                  "&:hover": { backgroundColor: analysed ? "#c8e6c9" : "#ffe0b2" },
                }}
                icon={
                  <Icon sx={{ fontSize: "13px !important", color: "inherit" }}>
                    {analysed ? "check_circle" : "pending"}
                  </Icon>
                }
              />
            </MDBox>
          );
          /* eslint-enable react/prop-types */
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          <ActionButtons
            row={row}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onViewDocs={handleViewDocs}
            onAnalyse={handleAnalyse}
          />
        ),
      },
    ],
    [domaines, types, handleEdit, handleDelete, handleViewDocs, handleToggleAnalyse, handleAnalyse]
  );

  const filteredRows = useMemo(() => {
    return rows
      .filter((r) => {
        const term = searchFilter.toLowerCase();
        const matchesSearch =
          !searchFilter.trim() ||
          r.titre?.toLowerCase().includes(term) ||
          r.reference?.toLowerCase().includes(term);
        const matchesDomaine = !domaineFilter || r.domaine_id === domaineFilter;
        const matchesType = !typeFilter || r.type_texte_id === typeFilter;
        return matchesSearch && matchesDomaine && matchesType;
      })
      .sort((a, b) => b.id - a.id);
  }, [rows, searchFilter, domaineFilter, typeFilter]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="purple"
            borderRadius="lg"
            coloredShadow="dark"
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
          >
            <MDBox>
              <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
                Gestion des Textes Réglementaires
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.72)", display: "block", mt: 0.3 }}
              >
                Catalogue des textes réglementaires UEMOA en vigueur
              </MDTypography>
            </MDBox>
            <MDButton variant="outlined" color="white" onClick={handleOpen}>
              Nouveau Texte
            </MDButton>
          </MDBox>
          <MDBox px={2} pt={2}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={4}>
                <MDInput
                  fullWidth
                  placeholder="Rechercher par titre ou référence..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon>search</Icon>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="text"
                  textTransform="uppercase"
                  sx={{ mb: 0.5, display: "block" }}
                >
                  Domaine
                </MDTypography>
                <FormControl fullWidth>
                  <Select
                    displayEmpty
                    value={domaineFilter}
                    onChange={(e) => setDomaineFilter(e.target.value)}
                    sx={{ height: "45px" }}
                  >
                    <MenuItem value="">Tous les domaines</MenuItem>
                    {domaines.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="text"
                  textTransform="uppercase"
                  sx={{ mb: 0.5, display: "block" }}
                >
                  Type d&apos;acte
                </MDTypography>
                <FormControl fullWidth>
                  <Select
                    displayEmpty
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    sx={{ height: "45px" }}
                  >
                    <MenuItem value="">Tous les types</MenuItem>
                    {types.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <MDButton
                  variant="gradient"
                  color="purple"
                  fullWidth
                  sx={{ height: "45px" }}
                  onClick={() => {
                    setSearchFilter("");
                    setDomaineFilter("");
                    setTypeFilter("");
                  }}
                >
                  <Icon sx={{ mr: 0.5 }}>restart_alt</Icon>
                  Reset
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>

          <MDBox pt={3}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" p={5}>
                <CircularProgress color="info" />
              </MDBox>
            ) : (
              <DataTable
                table={{ columns, rows: filteredRows }}
                isSorted
                entriesPerPage
                showTotalEntries
              />
            )}
          </MDBox>
        </Card>
      </MDBox>

      <Modal open={open} onClose={() => !isSubmitting && setOpen(false)}>
        <Box sx={{ ...modalStyle, animation: `${modalScaleIn} 0.3s ease-out both` }}>
          <MDTypography variant="h6" mb={2}>
            {isEdit ? "Modifier" : "Ajouter"} un Texte
          </MDTypography>
          <form onSubmit={handleSubmit}>
            {/* TITRE */}
            <MDBox mb={2}>
              <TextField
                fullWidth
                label="Titre"
                required
                variant="outlined"
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                inputProps={{ maxLength: 255 }}
                helperText={`${formData.titre.length}/255`}
              />
            </MDBox>

            {/* LIGNE DATE ET RÉFÉRENCE */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MDBox mb={2}>
                  <TextField
                    fullWidth
                    label="Date de Publication"
                    type="date"
                    required
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={formData.date_publication}
                    onChange={(e) => setFormData({ ...formData, date_publication: e.target.value })}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDBox mb={2}>
                  <TextField
                    fullWidth
                    label="Référence"
                    required
                    variant="outlined"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    inputProps={{ maxLength: 100 }}
                    helperText={`${formData.reference.length}/100`}
                  />
                </MDBox>
              </Grid>
            </Grid>

            {/* LIGNE DOMAINE ET TYPE */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <MDBox mb={2}>
                  <TextField
                    select
                    fullWidth
                    label="Domaine"
                    required
                    value={formData.domaine_id}
                    onChange={(e) => setFormData({ ...formData, domaine_id: e.target.value })}
                  >
                    {domaines.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.nom}
                      </MenuItem>
                    ))}
                  </TextField>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDBox mb={2}>
                  <TextField
                    select
                    fullWidth
                    label="Type"
                    required
                    value={formData.type_texte_id}
                    onChange={(e) => setFormData({ ...formData, type_texte_id: e.target.value })}
                  >
                    {types.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.type}
                      </MenuItem>
                    ))}
                  </TextField>
                </MDBox>
              </Grid>
            </Grid>

            {/* RÉSUMÉ - BIEN VISIBLE ICI */}
            <MDBox mb={2}>
              <TextField
                fullWidth
                label="Résumé (max 500 caractères)"
                multiline
                rows={4}
                variant="outlined"
                value={formData.resume}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, resume: e.target.value });
                  }
                }}
                helperText={`${formData.resume ? formData.resume.length : 0}/500`}
                error={formData.resume?.length > 500}
              />
            </MDBox>

            <MDBox mt={2}>
              <MDTypography variant="caption" fontWeight="bold">
                Fichier PDF {isEdit && "(Optionnel)"}
              </MDTypography>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                style={{ display: "block", marginTop: "8px", width: "100%" }}
              />
            </MDBox>

            <MDBox mt={3} display="flex" justifyContent="flex-end">
              <MDButton
                onClick={() => setOpen(false)}
                color="secondary"
                sx={{ mr: 1 }}
                disabled={isSubmitting}
              >
                Annuler
              </MDButton>
              <MDButton type="submit" variant="gradient" color="purple" disabled={isSubmitting}>
                {isSubmitting ? "Traitement..." : "Enregistrer"}
              </MDButton>
            </MDBox>
          </form>
        </Box>
      </Modal>

      {/* Modal Documents / Versions */}
      <Modal open={docsOpen} onClose={() => setDocsOpen(false)}>
        <Box
          sx={{
            ...modalStyle,
            width: { xs: "95vw", sm: "90vw", md: 700 },
            animation: `${modalScaleIn} 0.3s ease-out both`,
          }}
        >
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDTypography variant="h6">Documents - {selectedTexte?.titre}</MDTypography>
            <MDButton color="secondary" size="small" onClick={() => setDocsOpen(false)}>
              Fermer
            </MDButton>
          </MDBox>

          {docsLoading ? (
            <MDBox display="flex" justifyContent="center" p={4}>
              <CircularProgress color="info" />
            </MDBox>
          ) : versions.length === 0 ? (
            <MDTypography variant="body2" color="text" textAlign="center" py={3}>
              Aucune version trouvée.
            </MDTypography>
          ) : (
            versions.map((version) => (
              <MDBox key={version.id} mb={2}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  p={1.5}
                  sx={{
                    backgroundColor: version.actif ? "#f0fdf4" : "#fef2f2",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: version.actif ? "#bbf7d0" : "#fecaca",
                  }}
                >
                  <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <MDTypography variant="button" fontWeight="bold">
                      v{version.version}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {version.date_version
                        ? new Date(version.date_version).toLocaleDateString("fr-FR")
                        : ""}
                    </MDTypography>
                    <Chip
                      label={version.actif ? "Actif" : "Inactif"}
                      size="small"
                      color={version.actif ? "success" : "error"}
                      sx={{ ml: 1 }}
                    />
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDTypography variant="caption" sx={{ mr: 0.5 }}>
                      {version.actif ? "Désactiver" : "Activer"}
                    </MDTypography>
                    <Switch
                      checked={version.actif}
                      onChange={() => handleToggleVersion(version.id)}
                      color={version.actif ? "success" : "default"}
                    />
                    {!version.actif && (
                      <Tooltip title="Supprimer définitivement" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteVersion(version.id, version.version)}
                          sx={{ color: "#d32f2f", ml: 0.5 }}
                        >
                          <Icon sx={{ fontSize: "18px !important" }}>delete_forever</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                  </MDBox>
                </MDBox>

                {/* Documents de cette version */}
                {version.documents && version.documents.length > 0 ? (
                  version.documents.map((doc) => (
                    <MDBox
                      key={doc.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      px={2}
                      py={1}
                      ml={2}
                      mt={0.5}
                      sx={{
                        backgroundColor: "#fafafa",
                        borderRadius: 1,
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ minWidth: 0, flex: 1 }}
                      >
                        <Icon fontSize="small" color="info" sx={{ flexShrink: 0 }}>
                          picture_as_pdf
                        </Icon>
                        <MDBox sx={{ minWidth: 0 }}>
                          <MDTypography
                            variant="caption"
                            fontWeight="medium"
                            sx={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {doc.nom_fichier}
                          </MDTypography>
                          <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                            {doc.taille ? `${(doc.taille / 1024).toFixed(1)} Ko` : "N/A"}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <MDButton
                        size="small"
                        color="info"
                        variant="text"
                        onClick={() => handleDownload(doc)}
                      >
                        <Icon fontSize="small">download</Icon>
                      </MDButton>
                    </MDBox>
                  ))
                ) : (
                  <MDTypography variant="caption" color="text" sx={{ ml: 2, mt: 0.5 }}>
                    Aucun document
                  </MDTypography>
                )}
                <Divider sx={{ mt: 1.5 }} />
              </MDBox>
            ))
          )}
        </Box>
      </Modal>

      {/* Modal progression téléchargement */}
      <Modal open={downloadProgress.open} onClose={() => {}}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90vw", sm: 420 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <Icon sx={{ fontSize: "48px !important", color: "#186A81", mb: 1 }}>download</Icon>
          <MDTypography variant="h6" mb={0.5}>
            Téléchargement en cours...
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text"
            sx={{ display: "block", mb: 2, opacity: 0.65 }}
          >
            {downloadProgress.filename}
          </MDTypography>
          <LinearProgress
            variant={downloadProgress.progress > 0 ? "determinate" : "indeterminate"}
            value={downloadProgress.progress}
            sx={{ borderRadius: 2, height: 8, mb: 1 }}
            color="info"
          />
          {downloadProgress.progress > 0 && (
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
              {downloadProgress.progress}%
            </MDTypography>
          )}
        </Box>
      </Modal>

      {/* Modal progression upload */}
      <Modal open={isSubmitting && !!selectedFile} onClose={() => {}}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90vw", sm: 420 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <Icon sx={{ fontSize: "48px !important", color: "#186A81", mb: 1 }}>upload_file</Icon>
          <MDTypography variant="h6" mb={0.5}>
            Envoi du fichier en cours...
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text"
            sx={{ display: "block", mb: 2, opacity: 0.65 }}
          >
            {selectedFile?.name}
          </MDTypography>
          <LinearProgress
            variant={uploadProgress > 0 ? "determinate" : "indeterminate"}
            value={uploadProgress}
            sx={{ borderRadius: 2, height: 8, mb: 1 }}
            color="info"
          />
          {uploadProgress > 0 && (
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
              {uploadProgress}%
            </MDTypography>
          )}
        </Box>
      </Modal>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="secondary"
          >
            Annuler
          </MDButton>
          <MDButton
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm?.();
            }}
            variant="gradient"
            color="warning"
          >
            Confirmer
          </MDButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
}

export default TextesReglementaires;
